import { FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FilePreviewViewer, getPreviewType } from '../FilePreviewViewer/FilePreviewViewer';
import DownloadButton from '../DownloadButton';
import Spinner from '../Spinner/Spinner';
import BtButton from '../../../ts-components/bt-button/bt-button';
import { CrossSvg } from '../../svgs/cross';
import { ReactComponent as NavigationArrowIcon } from '../../../assets/svg/navigationArrowIcon.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/svg/downloadIcon.svg';
import styles from './FilePreviewOverlay.module.scss';

type FilePreviewOverlayProps = {
  url: string;
  fileName: string;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  counter?: string;
  /** Called when the user downloads the previewed file from the overlay. */
  onDownload?: () => void;
};

const FilePreviewOverlay: FC<FilePreviewOverlayProps> = ({
  url,
  fileName,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  counter,
  onDownload,
}) => {
  const [t] = useTranslation();
  const hasNav = !!(onPrevious || onNext);
  const showDownload = getPreviewType(fileName) !== 'pdf';

  return createPortal(
    <div className={styles.overlay} data-file-preview-overlay>
      <div className={styles.backdrop} onClick={onClose} aria-hidden />
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          title={t('button.close', 'Stäng')}
          aria-label={t('button.close', 'Stäng')}
        >
          <CrossSvg size="16px" />
        </button>
        <p className={styles.title}>{fileName}</p>
        <div className={styles.wrapper}>
          {hasNav && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.navLeft}`}
              onClick={onPrevious}
              disabled={!hasPrevious}
              title={t('button.previous', 'Föregående')}
            >
              <NavigationArrowIcon />
            </button>
          )}

          <div className={styles.content}>
            <FilePreviewViewer
              url={url}
              fileName={fileName}
              onClose={onClose}
              onPrevious={onPrevious}
              onNext={onNext}
            />
          </div>

          {hasNav && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.navRight}`}
              onClick={onNext}
              disabled={!hasNext}
              title={t('button.next', 'Nästa')}
            >
              <NavigationArrowIcon />
            </button>
          )}
        </div>

        {(counter || showDownload) && (
          <div className={styles.actions}>
            {counter && <span className={styles.counter}>{counter}</span>}
            {showDownload && (
              <DownloadButton
                url={url}
                fileName={fileName}
                render={({ onClick: downloadClick, downloading, progress }) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BtButton
                      color="white"
                      size="xxs"
                      onClick={downloadClick && (async () => {
                        await downloadClick();
                        onDownload?.();
                      })}
                      loaderShow={downloading}
                      leftIcon={<DownloadIcon />}
                    >
                      {t('button.download', 'Ladda ner')}
                    </BtButton>
                    {downloading && progress > 0 && progress < 1 && (
                      <Spinner
                        size="xxs"
                        progress={progress}
                        style={{ '--spinner-text-color': 'white', '--spinner-circle-color': 'white' } as React.CSSProperties}
                      />
                    )}
                  </div>
                )}
              />
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default FilePreviewOverlay;
